import { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Search } from '@mui/icons-material';
import { useCollection } from '../hooks/useFirestore';
import { query as fbQuery, where } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';

interface School {
  id: string;
  schoolName: string;
  email: string;
  phoneNumber: string;
  stripeConnectAccountId?: string;
  creditCardRevenue: number;
  isActive: boolean;
}

type StatusFilter = 'active' | 'suspended' | 'all';

export default function SchoolsPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  // デフォルトでは停止アカウントを非表示にする
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const { data: schools, loading } = useCollection<School>('users',
    (q) => fbQuery(q, where('role', '==', 'school'))
  );

  const columns: GridColDef[] = [
    { field: 'schoolName', headerName: '教室名', flex: 1 },
    { field: 'email', headerName: 'メールアドレス', flex: 1 },
    { field: 'phoneNumber', headerName: '電話番号', flex: 1 },
    {
      field: 'creditCardRevenue',
      headerName: 'クレジット決済収益',
      width: 150,
      renderCell: (params) => (
        (() => { const v = Number(params.value ?? 0); return `¥${v.toLocaleString()}`; })()
      ),
    },
    {
      field: 'isActive',
      headerName: 'ステータス',
      width: 100,
      renderCell: (params) => (
        params.value ? '有効' : '停止'
      ),
    },
  ];

  const filteredSchools = schools?.filter(school => {
    const name = (school.schoolName || '').toLowerCase();
    const email = (school.email || '').toLowerCase();
    const q = (searchQuery || '').toLowerCase();
    const matchesSearch = name.includes(q) || email.includes(q);

    // ステータス絞り込み（未設定は有効扱い）
    const active = school.isActive !== false;
    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'active' && active) ||
      (statusFilter === 'suspended' && !active);

    return matchesSearch && matchesStatus;
  });

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        教室一覧
      </Typography>

      <Card sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            fullWidth
            placeholder="教室名やメールアドレスで検索"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          <FormControl sx={{ minWidth: 180 }}>
            <InputLabel id="status-filter-label">ステータス</InputLabel>
            <Select
              labelId="status-filter-label"
              label="ステータス"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            >
              <MenuItem value="active">有効のみ</MenuItem>
              <MenuItem value="suspended">停止のみ</MenuItem>
              <MenuItem value="all">すべて</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Card>

      <Card sx={{ height: 'calc(100vh - 250px)' }}>
        <DataGrid
          rows={filteredSchools || []}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          onRowClick={(params) => navigate(`/schools/${params.id}`)}
        />
      </Card>
    </Box>
  );
}
